<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import PrioritySelector from "./prioritySelector.vue";
import ButtonBar from "../navigation/buttonBar.vue";
import ButtonTransparent from "../buttons/buttonTransparent.vue";

const emit = defineEmits(['created', 'updated', 'cancel']);

const props = defineProps({
    taskList: {
        type: Object,
        default: null,
        required: false,
    },
    task: {
        type: Object,
        default: null,
        required: false,
    }
})

const changingPriority = ref(false)

const _task = reactive({
    label: '',
    priority: null,
    available_at: null,
    available_at_time: null,
    available_at_utc: null,
    due_at: null,
    due_at_time: null,
    due_at_utc: null,
    notify_at_due_at_time: false,
    utc_offset_minutes: new Date().getTimezoneOffset(),
    type: 'todo',
    repeat_rule_type: null,
    routine_type: null,
    routine_properties: {
        type: 'weekdays',
        repeat_rule: null,
        days: null,
        weekdays: [],
    }
})

const mode = ref('create')

const createOrUpdateTask = async () => {
    if (_task.label.length === 0) {
        return
    }
    if (props.task === null) {
        const res = await axios.post(route('api.task.store', props.taskList.id), {
            task: _task
        })
        emit('created', res.data.task)
    } else {
        const res = await axios.put(route('api.task.update', props.task.id), {
            task: _task
        })
        emit('updated', res.data.task)
    }
}

const cancel = () => {
    emit('cancel')
}

const setPriority = (priorityLevel) => {
    _task.priority = priorityLevel
    changingPriority.value = false
}

const setAvailableAtTime = (time) => {
    _task.available_at_time = time
}
const setDueAtTime = (time) => {
    _task.due_at_time = time
}

const setTaskType = (type) => {
    if (type.name === 'routine') {
        _task.routine_properties.type = 'weekdays'
        _task.routine_properties.weekdays = []
    } else {
        _task.routine_type = null
        _task.routine_properties.type = null
        _task.routine_properties.weekdays = []
    }
    _task.type = type.name
}

const toggleWeekday = (day) => {
    const index = _task.routine_properties.weekdays.indexOf(day)
    if (index > -1) {
        _task.routine_properties.weekdays.splice(index, 1)
    } else {
        _task.routine_properties.weekdays.push(day)
    }
}

const assignTaskProperties = () => {
    _task.label = props.task.label
    _task.type = props.task.type
    _task.priority = props.task.priority
    _task.available_at = props.task.available_at?.date
    _task.available_at_time = props.task.available_at?.time
    _task.due_at = props.task.due_at?.date
    _task.due_at_time = props.task.due_at?.time
    _task.notify_at_due_at_time = props.task.notify_at_due_at_time
    _task.utc_offset_minutes = props.task.utc_offset_minutes
    _task.type = props.task.type
    _task.repeat_rule_type = props.task.repeat_rule_type
    _task.routine_type = props.task.routine_type
    if (props.task.routine_properties !== null) {
        _task.routine_properties = {
            type: props.task.routine_properties.type,
            repeat_rule: props.task.routine_properties.repeat_rule,
            days: props.task.routine_properties.days,
            weekdays: props.task.routine_properties.weekdays,
        }
    }
}

watch(() => _task.available_at, (newValue) => {
    if (! props.task && ! newValue) {
        _task.routine_type = null
        _task.routine_properties.type = null
        _task.routine_properties.days = null
        _task.type = 'todo'
    }
})

watch(() => _task.notify_at_due_at_time, (newValue) => {
    // if (! newValue) {
    //     _task.due_at_time = null
    // } else {
    //     if (props.task) {
    //         _task.due_at_time = props.task.due_at_time
    //     } else {
    //         _task.due_at_time = '09:00'
    //     }
    // }
})

const priorityIcon = computed(() => {
    const icons = {
        1: 'fa-solid fa-flag text-red-500',
        2: 'fa-solid fa-flag text-yellow-500',
        3: 'fa-solid fa-flag text-blue-500',
    }

    if (! _task.priority) {
        return 'fa-regular fa-flag text-gray-500';
    }
    if (_task.priority > 3) {
        return 'fa-solid fa-flag text-red-500';
    }

    return icons[_task.priority];
})

onMounted(() => {
    if (props.task) {
        assignTaskProperties()
    }
})

</script>

<template>
<div>
    <div class="flex items-start space-x-4">
        <input-text
            v-model:content="_task.label"
            placeholder="Task name"
            class="w-full "
            :error="_task.label.length > 0 ? '' : 'Task name is required'"
            autofocus
            @keyup.enter="createOrUpdateTask"
        ></input-text>
        <div class="pt-1.5 flex space-x-2">
            <div class="w-6 mt-2 relative">
                <button
                    class="h-5 flex justify-center items-center transition-opacity"
                    @click="changingPriority = !changingPriority"
                >
                    <i
                        :class="priorityIcon"
                    ></i>
                </button>

                <priority-selector
                    v-if="changingPriority"
                    class="absolute -bottom-0.5 -right-2"
                    @priority-selected="setPriority"
                    @cancel="changingPriority = false"
                ></priority-selector>
            </div>
            <button-transparent
                size="sm"
                @click="cancel"
            >
                <i class="fa-solid fa-xmark text-xl text-gray-400"></i>
            </button-transparent>
        </div>
    </div>

    <div>
        <button-bar
            class="mt-0.5"
            size="sm"
            :options="[
                { label: 'Todo', name: 'todo' },
                { label: 'Routine', name: 'routine' },
            ]"
            :selectedOptionName="_task.type"
            @select="setTaskType"
        />
    </div>

    <ui-tabbed
        :responsive="false"
        :track-state="false"
        v-if="_task.type === 'todo'"
    >
        <ui-tabbed-tab
            :active="true"
            value="dueDateTime"
            title="Due"
        >
            <div>
                <label
                    class="text-xs"
                >
                    Due date
                </label>
                <input-date
                    :content="_task.due_at"
                    @update:content="_task.due_at = $event"
                />
            </div>
            <div v-if="_task.due_at">
                <label
                    class="text-xs"
                >
                    Due time
                </label>
                <input-time
                    :content="_task.due_at_time"
                    @update:content="setDueAtTime"
                />
            </div>
            <div
                v-if="_task.due_at_time"
                class="col-span-2"
            >
                <toggle-switch
                    v-model:content="_task.notify_at_due_at_time"
                    label="Notify me when the task is due"
                />
            </div>
        </ui-tabbed-tab>
        <ui-tabbed-tab
            value="startDateTime"
            title="Start"
        >
            <div>
                <label
                    class="text-xs"
                >
                    Start date
                </label>
                <input-date
                    :content="_task.available_at"
                    @update:content="_task.available_at = $event"
                />
            </div>
            <div v-if="_task.available_at">
                <label
                    class="text-xs"
                >
                    Start time
                </label>
                <input-time
                    :content="_task.available_at_time"
                    @update:content="setAvailableAtTime"
                ></input-time>
            </div>
        </ui-tabbed-tab>
    </ui-tabbed>

    <div v-if="_task.type === 'routine'" class="space-y-4">
        <div v-if="_task.routine_properties?.weekdays" class="space-y-2">
            <label class="text-xs">Select weekdays</label>
            <div class="grid grid-cols-7 gap-2">
                <button
                    v-for="(day, index) in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']"
                    :key="index"
                    type="button"
                    @click="toggleWeekday(index + 1)"
                    :class="[
                        'px-2 py-2 text-xs font-medium rounded-md transition-colors',
                        _task.routine_properties.weekdays.includes(index+1)
                            ? 'bg-wlPrimary text-wlPrimaryContrast'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-200'
                    ]"
                >
                    {{ day }}
                </button>
            </div>
        </div>

        <div>
            <toggle-switch
                v-model:content="_task.notify_at_due_at_time"
                label="Notify me when the task is due"
            />
        </div>

        <div v-if="_task.notify_at_due_at_time">
            <label class="text-xs">Due time</label>
            <input-time
                :content="_task.due_at_time"
                @update:content="setDueAtTime"
            />
        </div>
    </div>

    <div class="flex space-x-2 justify-end">
        <button-success
            size="sm"
            @click="createOrUpdateTask"
            :disabled="_task.label.length === 0"
        >
            {{ props.task ? 'Update' : 'Create' }}
        </button-success>
    </div>
</div>
</template>
